import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utcPlugin);
dayjs.extend(customParseFormat);

type LocaleInfo = {
  startTime: string;
  endTime: string;
  placeholderTime: string;
  placeholderTimezone: string;
  customPlaceholder?: string;
};

type SupportedLocales = {
  [key: string]: LocaleInfo;
};

export const LOCALE_REGION_MAPPING: { [locale: string]: string } = {
  'en-au': 'APGCEnglish',
  'en-ca': 'default',
  'da-dk': 'EMEAEnglish',
  'en-in': 'APGCEnglish',
  'id-id': 'APGCEnglish',
  'en-ie': 'EMEAEnglish',
  'en-my': 'APGCEnglish',
  'nl-nl': 'EMEAEnglish',
  'en-nz': 'APGCEnglish',
  'nb-no': 'EMEAEnglish',
  'en-ph': 'APGCEnglish',
  'en-sg': 'APGCEnglish',
  'sv-se': 'EMEAEnglish',
  'en-gb': 'EMEAEnglish',
  'en-us': 'default',
  'vi-vn': 'APGCEnglish',
  'zh-cn': 'APGCEnglish',
  'fr-ca': 'EMEAFrench',
  'fr-fr': 'EMEAFrench',
  'de-at': 'EMEAGerman',
  'de-de': 'EMEAGerman',
  'de-ch': 'EMEAGerman',
  'it-it': 'EMEAItalian',
  'ja-jp': 'APGCJapanese',
  'pt-br': 'LATAMPortuguese',
  'es-ar': 'LATAMSpanish',
  'es-cl': 'LATAMSpanish',
  'es-co': 'LATAMSpanish',
  'es-mx': 'LATAMSpanish',
  'es-pe': 'LATAMSpanish',
  'es-es': 'EMEASpanish',
  'es-ve': 'LATAMSpanish',
};

export const LIVE_AGENT_SUPPORTED_LOCALES = [
  'en-au',
  'en-ca',
  'da-dk',
  'en-in',
  'id-id',
  'en-ie',
  'en-my',
  'en-nz',
  'nb-no',
  'en-ph',
  'en-sg',
  'sv-se',
  'en-gb',
  'en-us',
  'vi-vn',
];

export const LIVE_AGENT_LIMITED_SUPPORT_LOCALES: SupportedLocales = {
  'fr-ca': {
    startTime: '10:00',
    endTime: '17:00',
    placeholderTime: '5:00 AM-12:00 PM',
    placeholderTimezone: 'Eastern Standard Time',
  },
  'fr-fr': {
    startTime: '8:00',
    endTime: '17:00',
    placeholderTime: '9:00 AM-6:00 PM',
    placeholderTimezone: 'Central European Time',
  },
  'de-at': {
    startTime: '8:00',
    endTime: '17:00',
    placeholderTime: '9:00 AM-6:00 PM',
    placeholderTimezone: 'Central European Time',
  },
  'de-de': {
    startTime: '8:00',
    endTime: '17:00',
    placeholderTime: '9:00 AM-6:00 PM',
    placeholderTimezone: 'Central European Time',
  },
  'it-it': {
    startTime: '8:00',
    endTime: '17:00',
    placeholderTime: '9:00 AM-6:00 PM',
    placeholderTimezone: 'Central European Time',
  },
  'de-ch': {
    startTime: '8:00',
    endTime: '17:00',
    placeholderTime: '9:00 AM-6:00 PM',
    placeholderTimezone: 'Central European Time',
  },
  'ja-jp': {
    startTime: '2:00',
    endTime: '8:30',
    placeholderTime: '11:00 AM-5:30 PM',
    placeholderTimezone: 'Japan Standard Time',
  },
  'pt-br': {
    startTime: '11:00',
    endTime: '19:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Brasalia Time',
  },
  'es-ar': {
    startTime: '14:00',
    endTime: '22:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Central Time',
  },
  'es-cl': {
    startTime: '14:00',
    endTime: '22:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Central Time',
  },
  'es-co': {
    startTime: '14:00',
    endTime: '22:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Central Time',
  },
  'es-mx': {
    startTime: '14:00',
    endTime: '22:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Central Time',
  },
  'es-pe': {
    startTime: '14:00',
    endTime: '22:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Central Time',
  },
  'es-es': {
    startTime: '8:00',
    endTime: '17:00',
    placeholderTime: '9:00 AM-6:00 PM',
    placeholderTimezone: 'Central European Time',
  },
  'es-ve': {
    startTime: '14:00',
    endTime: '22:30',
    placeholderTime: '8:00 AM-4:30 PM',
    placeholderTimezone: 'Central Time',
  },
  'nl-nl': {
    startTime: '8:00',
    endTime: '16:30',
    placeholderTime: '9:00 AM-5:30 PM',
    placeholderTimezone: 'Central European Time',
  },
};

export type HolidayCloseHoursLocaleMap = {
  [locale: string]: {
    [date: string]: {
      utcEndTime: string;
    } | 'closed';
  };
};

export const LIVE_AGENT_HOLIDAY_CLOSE_HOURS_LOCALE_MAP: HolidayCloseHoursLocaleMap = {
  'it-it': {
    '2025-04-21': 'closed',
  },
  'nl-nl': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'fr-fr': {
    '2025-04-21': 'closed',
  },
  'fr-ca': {
    '2025-04-21': 'closed',
  },
  'de-ch': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'de-at': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'de-de': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'es-es': {
    '2025-04-18': 'closed',
  },
  'es-co': {
    '2025-04-18': 'closed',
  },
  'es-mx': {
    '2025-04-18': 'closed',
  },
  'es-ar': {
    '2025-04-18': 'closed',
  },
  'es-cl': {
    '2025-04-18': 'closed',
  },
  'es-pe': {
    '2025-04-18': 'closed',
  },
  'es-ve': {
    '2025-04-18': 'closed',
  },
  'pt-br': {
    '2025-04-18': 'closed',
  },
};

export const LIVE_AGENT_OPTIMIZATION_QUEUE_HOLIDAY_CLOSE_HOURS_LOCALE_MAP: HolidayCloseHoursLocaleMap = {
  'it-it': {
    '2025-04-21': 'closed',
  },
  'nl-nl': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'fr-fr': {
    '2025-04-21': 'closed',
  },
  'fr-ca': {
    '2025-04-21': 'closed',
  },
  'de-ch': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'de-at': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'de-de': {
    '2025-04-18': 'closed',
    '2025-04-21': 'closed',
  },
  'es-es': {
    '2025-04-18': 'closed',
  },
  'es-co': {
    '2025-04-18': 'closed',
  },
  'es-mx': {
    '2025-04-18': 'closed',
  },
  'es-ar': {
    '2025-04-18': 'closed',
  },
  'es-cl': {
    '2025-04-18': 'closed',
  },
  'es-pe': {
    '2025-04-18': 'closed',
  },
  'es-ve': {
    '2025-04-18': 'closed',
  },
  'pt-br': {
    '2025-04-18': 'closed',
  },
};

export const isCustomerSupportClosedForHoliday = (
  locale: string,
  holidayCloseHoursLocaleMap: HolidayCloseHoursLocaleMap,
): boolean => {
  const currentDateTime = dayjs.utc();
  const currentDate = currentDateTime.format('YYYY-MM-DD');
  const endTime = holidayCloseHoursLocaleMap[locale]?.[currentDate];

  // if the date is not in the map, then the support is available
  if (endTime == null) return false;

  // if the date is in the map and the value is 'closed', then the support is closed
  if (endTime === 'closed') return true;

  const { utcEndTime } = endTime;
  // if the date is in the map and current time is beyond the working hours, then the support is closed
  const formattedEndTime = dayjs.utc(utcEndTime, 'HH:mm');
  return currentDateTime.isAfter(formattedEndTime);
};

/**
 * `isDSTEnabled` checks if Daylight Saving Time is enabled for the given date in the given region.
 * None of the region observing DST, observe DST on both 1st January and 1st July.
 * So, we can check the offset of these dates to determine if DST is enabled and considers one with the maximum offset.
 * If maximum offset is greater than the current offset, then DST is being observed for the given date.
 * @param {String} date Date for which DST needs to be checked. Default is current date.
 * @returns {Boolean} `true` if DST is enabled, `false` otherwise.
 */
export const isDSTEnabled = (date = '') => {
  const newDate = date ? new Date(date) : new Date();
  const jan = new Date(newDate.getFullYear(), 0, 1).getTimezoneOffset();
  const jul = new Date(newDate.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) > newDate.getTimezoneOffset();
};

/**
 * `updateTimeRangeForDST` updates the start and end time for the given date if DST is enabled.
 * It subtracts 1 hour from the start and end time if DST is enabled.
 * @param {Object} startTime Start time in HH:mm format.
 * @param {Object} endTime End time in HH:mm format.
 * @returns {Object} Updated start and end time.
 */
export const updateTimeRangeForDST = ({
  startTime,
  endTime,
}: {
  startTime: string;
  endTime: string;
}) => {
  const isDST = isDSTEnabled();
  if (!isDST) {
    return { startTime, endTime };
  }

  return {
    startTime: dayjs(startTime, 'H:mm').subtract(1, 'hour').format('H:mm'),
    endTime: dayjs(endTime, 'H:mm').subtract(1, 'hour').format('H:mm'),
  };
};

// Check if the day is Monday to Friday
const isWorkingDay = (day: number) => day > 0 && day < 6;
const isWithinTimeRange = (currentTime: any, startTime: any, endTime: any) => (currentTime.isSame(startTime) || currentTime.isAfter(startTime))
  && currentTime.isBefore(endTime);

export const isCustomerSupportAvailable = (
  locale: string,
  startTime: string,
  endTime: string,
): boolean => {
  const currentMoment = dayjs()
    .locale(locale)
    .utc(); // Convert to UTC
  const currentDay = currentMoment.day(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday

  // Check if it's a working day
  if (!isWorkingDay(currentDay)) {
    return false;
  }

  // Check if it's within any support time range
  // We offset the UTC difference by adding current day, since it gets reset to original date on enforcing utc
  const startTimeInUTC = dayjs.utc(startTime, 'HH:mm').day(currentDay);
  const endTimeInUTC = dayjs.utc(endTime, 'HH:mm').day(currentDay);

  // Handle the case where end time is on the next day
  if (endTimeInUTC.isBefore(startTimeInUTC)) {
    const nextDayEndTime = endTimeInUTC.clone().add(1, 'day');
    if (
      isWithinTimeRange(currentMoment, startTimeInUTC, nextDayEndTime)
      || isWithinTimeRange(
        currentMoment,
        startTimeInUTC.subtract(1, 'day'),
        endTimeInUTC,
      )
    ) {
      return true;
    }
  } else if (isWithinTimeRange(currentMoment, startTimeInUTC, endTimeInUTC)) {
    return true;
  }

  return false;
};

export const isLiveChatSupportOpen = (locale: string | undefined): boolean => {
  if (!locale) return false;

  const lowerLocale = locale.toLocaleLowerCase();

  if (isCustomerSupportClosedForHoliday(lowerLocale, LIVE_AGENT_HOLIDAY_CLOSE_HOURS_LOCALE_MAP)) {
    return false;
  }

  if (LIVE_AGENT_SUPPORTED_LOCALES.includes(lowerLocale)) {
    return true;
  }
  if (LIVE_AGENT_LIMITED_SUPPORT_LOCALES[lowerLocale]) {
    const localeInfo = LIVE_AGENT_LIMITED_SUPPORT_LOCALES[lowerLocale];
    const { startTime, endTime } = updateTimeRangeForDST({
      startTime: localeInfo.startTime,
      endTime: localeInfo.endTime,
    });
    return isCustomerSupportAvailable(lowerLocale, startTime, endTime);
  }
  return false;
};

export const isLocaleSupported = (locale: string | undefined): boolean => {
  if (!locale) return false;

  if (
    LIVE_AGENT_SUPPORTED_LOCALES.includes(locale)
    || locale in LIVE_AGENT_LIMITED_SUPPORT_LOCALES
  ) {
    return true;
  }
  return false;
};

const ENGLISH_ONLY_SUPPORTED_LOCALES = [
  'hi-in', 'cs-cz', 'fi-fi', 'ko-kr', 'th-th',
  'el-gr', 'fil-ph', 'pl-pl', 'ro-ro', 'uk-ua',
  'nb-no', 'hu-hu', 'ms-my', 'ru-ru', 'tr-tr',
  'bg-bg', 'hr-hr', 'lt-lt', 'zh-cn', 'zh-hk', 'zh-tw',
];

export const isEnglishOnlyChatSupportedLocale = (locale?: string): boolean => {
  if (!locale) return false;

  const lowerLocale = locale.toLocaleLowerCase();
  return ENGLISH_ONLY_SUPPORTED_LOCALES.includes(lowerLocale);
};

const LOCALES_TO_SKIP_LIVE_CHAT_SUPPORT_CHECK: string[] = [];

export const shouldSkipLiveChatSupportOpenCheck = (
  locale?: string,
  skipLocaleList: string[] = LOCALES_TO_SKIP_LIVE_CHAT_SUPPORT_CHECK,
): boolean => {
  if (!locale) return false;
  if (!skipLocaleList.length) return false;

  const skipLowerLocaleList = skipLocaleList.map((skipLocale) => skipLocale.toLocaleLowerCase());
  const lowerLocale = locale.toLocaleLowerCase();
  return skipLowerLocaleList.includes(lowerLocale);
};
